import RichEditorWithSuggestions from "components/ResumeForm/Form/RichEditorWithSuggestions";
import { useAppSelector } from "lib/redux/hooks";
import { changeSkills, selectSkills } from "lib/redux/resumeSlice";
import Image from "next/image";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { SectionHeader } from "../common/SectionHeader";
// import { NoIssueBox } from "../common/NoIssueBox";
import { SkillScoreAnalysis } from "lib/resume-scoring/types";
import { highlightSection, clearHighlight } from "lib/redux/highlightSlice";
import {
  selectUserProfilePhoto,
  setIsAuthenticationOpen,
  setOAuthOpenedFromButton,
} from "lib/redux/uiSlice";
import { sanitizeResume } from "lib/utils/sanitizeResume";
import {
  changeProfileStatus,
  selectResume,
  setResume,
} from "lib/redux/resumeSlice";
import { createResume, updateResume } from "api/resumeApi";
import axios from "axios";
import { store } from "lib/redux/store";
import { saveStateToLocalStorage } from "lib/redux/local-storage";
import { setResumeCache } from "../../utils/localStorageHelper";
import CustomIcon from "components/CustomIcon";

export const SkillsSection = ({
  score,
  maxScore,
  analysis,
}: {
  score: SkillScoreAnalysis;
  maxScore: number;
  analysis: { descriptionAnalysis: { statement: string }[] };
}) => {
  const [isAddSkillOpen, setIsAddSkillOpen] = useState(false);
  const skills = useAppSelector(selectSkills);
  const { descriptions } = skills;
  const dispatch = useDispatch();
  const resume = useAppSelector(selectResume);
  const userProfilePhoto = useAppSelector(selectUserProfilePhoto);
  const handleSaveResume = async () => {
    if (!isAddSkillOpen) return;
    dispatch(setOAuthOpenedFromButton("save"));
    try {
      const access_token = localStorage.getItem("access-token");
      if (!access_token) {
        dispatch(setIsAuthenticationOpen(true));
        localStorage.setItem("auth-overlay", "false");
        return;
      }
      const sanitizedResume = sanitizeResume(resume);

      if (resume.id) {
        const updatedResume = await updateResume(
          sanitizedResume,
          resume.id,
          userProfilePhoto!
        );

        // Update Redux state
        dispatch(setResume(updatedResume));

        // Update resumeCache in localStorage
        setResumeCache({ resume: updatedResume });

        // CRITICAL: Update localStorage with the correct key used by useSetInitialStore
        // This ensures navigation to builder will load the fresh data
        const currentState = store.getState();
        saveStateToLocalStorage({
          ...currentState,
          resume: updatedResume,
        });
      } else {
        const createdResume = await createResume(
          sanitizedResume,
          userProfilePhoto!
        );

        // Update Redux state
        dispatch(setResume(createdResume));

        // Update resumeCache in localStorage
        setResumeCache({ resume: createdResume });

        // CRITICAL: Update localStorage with the correct key
        const currentState = store.getState();
        saveStateToLocalStorage({
          ...currentState,
          resume: createdResume,
        });
      }
      dispatch(changeProfileStatus("IGNORED"));
    } catch (error: any) {
      if (error.code === "TOKEN_EXPIRED") {
        dispatch(setIsAuthenticationOpen(true));
      }
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        const createdResume = await createResume(resume, userProfilePhoto!);
        dispatch(setResume(createdResume));

        // Update resumeCache in localStorage
        setResumeCache({ resume: createdResume });

        // Update localStorage after error recovery
        const currentState = store.getState();
        saveStateToLocalStorage({
          ...currentState,
          resume: createdResume,
        });
      }
    }
  };

  const handleSkillsChange = (field: "descriptions", value: string[]) => {
    dispatch(changeSkills({ field, value }));
  };

  const handleSectionMouseEnter = () => {
    dispatch(
      highlightSection({
        section: "skills",
        itemIndex: null,
      })
    );
  };

  const handleSectionMouseLeave = () => {
    dispatch(clearHighlight());
  };

  return (
    <div className="scrollbar-hide relative mb-4 flex h-full max-h-[88.5vh] flex-col rounded-[12px] bg-white p-1 shadow-md sm:p-2 lg:rounded-[6px] lg:p-1 xl:p-2">
      {/* Fixed Sticky Header */}
      <SectionHeader
        title="Skills"
        score={score.totalScore}
        maxScore={maxScore}
      />
      <span
        className="sticky top-0 z-10 mx-auto my-2 hidden cursor-pointer  items-center justify-center gap-3 rounded-xl bg-gray-100 px-[16px] py-[4px] text-center text-sm font-semibold text-black shadow-md transition-opacity hover:opacity-90 md:text-lg lg:flex lg:px-[20px] lg:py-[8px] xl:px-[24px] xl:text-xl"
        onMouseEnter={handleSectionMouseEnter}
        onMouseLeave={handleSectionMouseLeave}
      >
        Skills
      </span>

      {/* Scrollable Content - Hidden Scrollbar */}
      <div className="scrollbar-hide mt-2 flex flex-1 flex-col gap-[25px] overflow-auto px-0 pb-3 xl:gap-[30px] xl:px-0 2xl:gap-[40px]">
        <div className="flex flex-col gap-6">
          {analysis.descriptionAnalysis.length > 0 ? (
            <div className="relative mt-4 flex flex-col gap-[8px] rounded-xl bg-[#FEE7E4] px-[10px] py-[8px] sm:gap-[10px] sm:px-[15px] sm:py-[10px] md:mt-3 lg:gap-[16px] lg:px-[20px] lg:py-[14px]">
              <div className="absolute left-4 top-[-16px] flex items-center gap-2 rounded-xl bg-white px-4 py-2 font-bold text-black shadow-md lg:left-8 lg:px-6">
                <Image
                  src={`/icons/score-check/missing.svg`}
                  height={24}
                  width={24}
                  alt="icon"
                  className="h-4 w-4 lg:h-5 lg:w-5"
                />
                <span className="text-sm font-bold lg:text-base xl:text-lg">
                  Skills Found
                </span>
              </div>
              <ul className="mt-4 gap-y-[10px] pl-[15px] marker:text-lg marker:text-[#92C5FF] sm:gap-y-[10px] sm:pl-[25px] sm:marker:text-2xl lg:gap-y-[20px] lg:pl-[15px] lg:marker:text-lg xl:gap-y-[20px] xl:pl-[25px] xl:marker:text-2xl">
                {analysis.descriptionAnalysis.map((skill, index) => (
                  <li
                    key={index}
                    className="my-[6px] flex w-full cursor-pointer items-center gap-2 rounded-lg bg-[#FDFDFD] p-2 pl-2 text-xs font-medium text-[#92C5FF] transition-opacity hover:opacity-80 md:text-base lg:my-[6px] lg:gap-4 lg:text-base xl:my-[10px] xl:pl-3 "
                  >
                    {skill.statement}
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex flex-col items-center justify-center gap-1">
                {/* <p className="text-center my-2 text-sm font-semibold text-green-600 lg:text-base">
                  No missing skills detected
                </p> */}
                <div className="relative my-2 h-32 w-44">
                  <Image
                    src="/assets/resumeScore/NoMissingInfo.png"
                    alt="Word mistakes illustration"
                    fill
                    className="w-15 h-10"
                  />
                </div>
                <p className="text-center text-xs text-gray-600 lg:text-sm">
                  Great job including relevant skills in your resume!
                </p>
              </div>
            </div>
          ) : (
            <div className="relative mt-4 flex flex-col gap-[8px] rounded-xl bg-[#FEE7E4] px-[10px] py-[8px] sm:gap-[10px] sm:px-[15px] sm:py-[10px] md:mt-3 lg:gap-[16px] lg:px-[20px] lg:py-[14px]">
              <div className="absolute left-4 top-[-16px] flex items-center gap-2 rounded-xl bg-white px-4 py-2 font-bold text-black shadow-md lg:left-8 lg:px-6">
                <Image
                  src={`/icons/score-check/missing.svg`}
                  height={24}
                  width={24}
                  alt="icon"
                  className="h-4 w-4 lg:h-5 lg:w-5"
                />
                <span className="text-sm font-bold lg:text-base xl:text-lg">
                  Missing Skills
                </span>
              </div>

              <div className="mt-4 flex flex-col items-center justify-center gap-1">
                <p className="my-2 text-center text-sm font-semibold text-red-600 lg:text-base">
                  Missing skills can make your resume less competitive
                </p>
                <div className="relative my-2 h-32 w-44">
                  <Image
                    src="/assets/resumeScore/missingInfo.png"
                    alt="Word mistakes illustration"
                    fill
                    className="w-15 h-10"
                  />
                </div>
                <p className="text-center text-xs text-gray-600 lg:text-sm">
                  Including relevant skills can improve your resume's appeal
                </p>
              </div>
            </div>
          )}

          {/* Add Skills */}
          <div className="lg:py-[18px flex w-full flex-col items-center gap-3 rounded-xl bg-[#EAF1F4] p-[15px] sm:p-[20px] lg:px-[25px]">
            <div className="flex w-full items-center justify-between">
              <span className="text-sm font-semibold text-gray-600 sm:text-base md:text-lg lg:text-xl">
                Edit Skills
              </span>
              <button
                onClick={() => setIsAddSkillOpen(!isAddSkillOpen)}
                className="rounded-lg bg-white px-3 py-1 text-xs font-medium text-[#458ef7] shadow-md hover:bg-gray-100 sm:px-4 sm:py-2 sm:text-sm md:text-base lg:px-5"
              >
                {isAddSkillOpen ? "Close" : "Edit"}
              </button>
            </div>

            {isAddSkillOpen && (
              // <div>
                <div className="mx-auto mt-2 flex w-full flex-col items-center justify-center gap-3 rounded-xl bg-white p-2 sm:p-3 lg:p-4">
                  <RichEditorWithSuggestions
                    name="descriptions"
                    value={descriptions}
                    onChange={handleSkillsChange}
                    placeholder="List your Skills here"
                    section="skills"
                  />
                {/* </div> */}
                <div className="my-1 flex w-full items-center justify-center gap-2 sm:gap-3 md:gap-5 lg:gap-10">
                  <button
                    onClick={() => {
                      handleSaveResume();
                    }}
                    className="flex items-center gap-1 rounded-full bg-white px-4 py-1 text-xs font-medium text-black shadow-md sm:gap-2 sm:px-5 sm:py-2 sm:text-sm md:px-6 md:text-base lg:gap-3 lg:px-[36px] lg:py-[8px] lg:text-lg"
                  >
                    Save
                    <CustomIcon
                      iconName="importBlack"
                      className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5"
                    />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add custom scrollbar hide styles */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};
